<script setup lang="ts">
import { computed } from "vue";
import { AwIcon, AwSkeleton } from "@asap/design";
import { deltaStatus, formatNumber, statusColorName, statusGradientClass, type ChartStatus } from "./theme";

const RING_SIZE = 120;
const RING_STROKE_WIDTH = 3;

const props = withDefaults(
  defineProps<{
    title: string;
    displayValue?: string;
    delta?: number;
    comparison?: string;
    achievementPercent?: number;
    target?: string;
    status?: ChartStatus;
    loading?: boolean;
  }>(),
  {
    status: "warning",
    loading: false,
    displayValue: undefined,
    delta: undefined,
    comparison: undefined,
    achievementPercent: undefined,
    target: undefined,
  }
);

const colorName = computed(() => statusColorName(props.status));

const metrics = computed(() => {
  if (props.delta === undefined || props.achievementPercent === undefined || props.displayValue === undefined) {
    return null;
  }

  return {
    displayValue: props.displayValue,
    achievementPercent: props.achievementPercent,
    deltaMagnitude: formatNumber(Math.abs(props.delta), 1),
    deltaColorName: statusColorName(deltaStatus(props.delta)),
    deltaIcon: props.delta > 0 ? "i-ph-caret-up-fill" : props.delta < 0 ? "i-ph-caret-down-fill" : null,
  };
});

const containerClass = computed(() =>
  !props.loading && metrics.value
    ? [statusGradientClass(props.status), `border-${colorName.value}-100`]
    : ["bg-white", "border-secondary-200"]
);

const ui = {
  container: "flex items-center gap-4 rounded-xl border p-4",
  chartSlot: "min-w-0 flex-1",
  displayValue: "text-2xl font-semibold leading-none text-secondary-900",
  displayLabel: "mt-1 text-xs text-secondary-700",
  sidePanel: "flex min-w-0 flex-col items-start gap-2",
  deltaRow: "flex items-center gap-1 text-sm",
  deltaIcon: "size-3",
  deltaValue: "font-medium",
  comparison: "text-secondary-700",
  targetPill: "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
  targetPercent: "font-medium",
  targetLabel: "text-secondary-700",
  skeletonChart: "h-[140px] w-full rounded-lg",
};
</script>

<template>
  <ConsultantKpiCard :title="title" flat>
    <div :class="[ui.container, containerClass]">
      <AwSkeleton v-if="loading" :class="ui.skeletonChart" />

      <template v-else-if="metrics">
        <ConsultantKpiProgressRing
          :percent="metrics.achievementPercent"
          :status="status"
          :size="RING_SIZE"
          :stroke-width="RING_STROKE_WIDTH"
          tinted-track
        >
          <span :class="ui.displayValue">{{ metrics.displayValue }}</span>
          <span :class="ui.displayLabel">Taux de Marge</span>
        </ConsultantKpiProgressRing>

        <div :class="ui.sidePanel">
          <div :class="ui.deltaRow">
            <AwIcon
              v-if="metrics.deltaIcon"
              :name="metrics.deltaIcon"
              :class="[ui.deltaIcon, `text-${metrics.deltaColorName}-600`]"
            />
            <span :class="[ui.deltaValue, `text-${metrics.deltaColorName}-600`]">{{ metrics.deltaMagnitude }}</span>
            <span v-if="comparison" :class="ui.comparison">{{ comparison }}</span>
          </div>

          <div :class="[ui.targetPill, `bg-${colorName}-100`]">
            <span :class="[ui.targetPercent, `text-${colorName}-600`]">{{ metrics.achievementPercent }}%</span>
            <span v-if="target" :class="ui.targetLabel">{{ target }}</span>
          </div>
        </div>
      </template>

      <div v-else :class="ui.chartSlot">
        <slot></slot>
      </div>
    </div>
  </ConsultantKpiCard>
</template>
